import { createFileRoute, Link } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import {
  FiArrowRight,
  FiCalendar,
  FiCheck,
  FiChevronDown,
  FiFileText,
  FiMenu,
  FiMessageCircle,
  FiRefreshCw,
  FiShield,
  FiX,
} from 'react-icons/fi';
import { Footer, SEO } from '@/components';
import { track } from '@/lib/analytics';
import { apiFetch } from '@/lib/api';
import { getDivorceJurisdictionByCode, isPacketState } from '@/lib/divorce';
import { requireDivorceProduct } from '@/lib/divorce-gate';
import {
  DOC_PREP_DISCLAIMER,
  REFUND_POLICY_SUMMARY,
} from '@/lib/divorce-legal';
import shipSvg from '@/assets/ship.svg';
import harborSsCalendarPng from '@/assets/harbor_ss_calendar.png';
import harborSsChatPng from '@/assets/harbor_ss_chat.png';
import harborSsExportsPng from '@/assets/harbor_ss_exports.png';

const nunito = {
  fontFamily:
    "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
};

const khula = {
  fontFamily:
    "'Khula', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
};

const LOCAL_DIVORCE_TOKEN_KEY = 'harbor.saved-flow';
const DivorceResumeContext = createContext<string | null>(null);

/**
 * Copy for the optional state-specific section. Only packet-supported states
 * get an entry; the section is hidden without a matching ?state= param.
 */
const STATE_LANDING_COPY: Record<
  string,
  {
    eyebrow: string;
    heading: string;
    body: string;
    bullets: string[];
    goodFit: string[];
    counselNote: string;
  }
> = {
  CA: {
    eyebrow: 'Built for California',
    heading: 'Built for the California joint petition',
    body: 'Harbor’s California path is the 2026 joint petition: both of you sign FL-700 and FL-710, and no one has to be served. Short marriages with no children and little property may qualify for the court’s separate summary dissolution process. That is not Harbor’s kit.',
    bullets: [
      '6 months in California and 3 months in your filing county',
      'Court filing fee is $870 (two first-paper fees); fee waivers exist',
      'Six-month waiting period after the joint petition is filed',
    ],
    goodFit: [
      'You both want a divorce, not an annulment',
      'You can sign the joint petition together',
      'You are working toward agreement on kids, property, and support',
    ],
    counselNote: 'If the case is contested, hire counsel licensed in California.',
  },
};

const faqItems = [
  {
    name: 'What is the free fit check?',
    text: 'A short set of yes-or-no questions that uses the same eligibility rules as the rest of Harbor. Harbor may fit, you may need to wait, or we will point you to better help. It is free and does not create a court filing.',
  },
  {
    name: 'What do I get for $299?',
    text: 'One case price covers petition through judgment — whether you start at filing or already filed. Harbor fills the official forms from the answers you confirm, regenerates them if your state updates those forms, and includes 12 months of Harbor Pro for messaging, expenses, and a custody calendar after you file.',
  },
  {
    name: 'Which states does Harbor support?',
    text: 'Harbor prepares filled divorce forms in California today, with more states on the way. The free fit check works everywhere, and Harbor publishes a free divorce checklist for every state.',
  },
  {
    name: 'Is this legal advice?',
    text: DOC_PREP_DISCLAIMER,
  },
  {
    name: 'What is an uncontested divorce?',
    text: 'A divorce where both spouses agree, or are working to agree, on the major terms: property, support, and parenting. Uncontested cases can usually be completed with court self-help forms, without hiring a lawyer for full representation.',
  },
  {
    name: 'Does $299 include the court filing fee?',
    text: 'No. Harbor’s $299 is for preparing the official forms from the answers you confirm. Court filing fees are separate and vary by state and county. Fee waivers exist if you qualify based on income. Harbor does not file with the court or appear for you.',
  },
  {
    name: 'Can I get a refund?',
    text: REFUND_POLICY_SUMMARY,
  },
  {
    name: 'What if we have children?',
    text: 'Courts expect a parenting plan in any divorce with minor children. You can build one free in Harbor, then bring it into your divorce answers so the same schedule language shows up on your forms.',
  },
  {
    name: 'How long does this take?',
    text: 'The fit check takes a couple of minutes. Most people finish the rest of the questions in under an hour. Your draft saves automatically after every step, so you can stop and pick it up later from the same link.',
  },
  {
    name: 'How do I get my packet?',
    text: 'After payment, your filled forms are generated and you download the PDF packet directly from your review page. You can re-download it any time with your resume link.',
  },
  {
    name: 'Do both spouses need an account?',
    text: 'No. One of you answers the questions and downloads the packet. Forms that need both signatures are signed on paper before you file.',
  },
];

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqItems.map((item) => ({
    '@type': 'Question',
    name: item.name,
    acceptedAnswer: { '@type': 'Answer', text: item.text },
  })),
};

const breadcrumbJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://harborcoparent.com/' },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'Harbor Divorce',
      item: 'https://harborcoparent.com/divorce',
    },
  ],
};

const productJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Product',
  name: 'Harbor Divorce',
  description:
    'Self-help divorce document preparation: a free fit check, official court forms filled from your answers, and 12 months of Harbor Pro. Packet preparation is available in California today.',
  brand: { '@type': 'Brand', name: 'Harbor' },
  offers: {
    '@type': 'Offer',
    price: '299.00',
    priceCurrency: 'USD',
    availability: 'https://schema.org/InStock',
    url: 'https://harborcoparent.com/divorce',
  },
};

export const Route = createFileRoute('/divorce/')({
  validateSearch: (search: Record<string, unknown>): { state?: string } => ({
    state:
      typeof search.state === 'string' && getDivorceJurisdictionByCode(search.state)
        ? search.state.toUpperCase()
        : undefined,
  }),
  beforeLoad: requireDivorceProduct,
  component: DivorceLandingPage,
});

function trackNav(section: string) {
  track('Divorce Landing Nav Clicked', { section });
}

function StartInterviewButton({
  location,
  className,
  children,
}: {
  location: string;
  className: string;
  children: ReactNode;
}) {
  const { state } = Route.useSearch();
  const resumeToken = useContext(DivorceResumeContext);
  return (
    <Link
      to='/divorce/interview'
      search={{
        claimToken: resumeToken ?? undefined,
        state: resumeToken ? undefined : state,
        planToken: undefined,
        fit: resumeToken ? undefined : 'true',
        purchase: undefined,
      }}
      onClick={() => {
        track('Divorce Landing Interview CTA Clicked', {
          ...(state ? { state } : {}),
          location,
        });
        const w = window as unknown as {
          gtagSendEvent?: (n: string) => void;
          fbq?: (...a: unknown[]) => void;
        };
        if (typeof w.gtagSendEvent === 'function') w.gtagSendEvent('start_divorce_interview');
        if (typeof w.fbq === 'function') w.fbq('trackCustom', 'StartDivorceInterview', { location });
      }}
      className={className}
      style={{ ...nunito, fontWeight: 600 }}
    >
      {resumeToken ? (
        <>
          Resume your application
          <FiArrowRight />
        </>
      ) : (
        children
      )}
    </Link>
  );
}

function DivorceHeader() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const linkClass = 'text-white/90 hover:text-white transition-colors font-medium';

  return (
    <header className='fixed top-0 left-0 right-0 z-50 bg-primary/95 backdrop-blur-sm'>
      <nav className='mx-auto max-w-7xl px-6 py-4'>
        <div className='flex items-center justify-between'>
          <Link
            to='/'
            search={{ hero: undefined }}
            className='flex items-center gap-2 text-2xl font-light leading-none tracking-tight text-white'
            style={khula}
          >
            <img src={shipSvg} alt='' className='mr-3 h-5 w-5' />
            <span className='mt-2'>Harbor</span>
          </Link>

          <div className='hidden items-center gap-8 md:flex'>
            <a href='#fit-check' onClick={() => trackNav('Fit check')} className={linkClass} style={nunito}>
              Fit check
            </a>
            <a href='#how-it-works' onClick={() => trackNav('How it works')} className={linkClass} style={nunito}>
              How it works
            </a>
            <a href='#pricing' onClick={() => trackNav('Pricing')} className={linkClass} style={nunito}>
              Pricing
            </a>
            <Link
              to='/divorce/case'
              search={{ id: undefined, invite: undefined, claim: undefined }}
              onClick={() => track('Divorce Landing Case CTA Clicked', { location: 'Header' })}
              className='text-white/90 hover:text-white font-medium'
              style={nunito}
            >
              Already filed?
            </Link>
            <StartInterviewButton
              location='Header'
              className='rounded-xl bg-white px-5 py-2 font-semibold text-primary transition-colors hover:bg-white/90'
            >
              Get Started
            </StartInterviewButton>
          </div>

          <button
            type='button'
            className='p-2 text-white md:hidden'
            onClick={() => setMobileMenuOpen((open) => !open)}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {mobileMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
          </button>
        </div>

        {mobileMenuOpen ? (
          <div className='mt-4 flex flex-col gap-4 border-t border-white/20 pt-4 pb-2 md:hidden'>
            <a
              href='#fit-check'
              onClick={() => {
                trackNav('Fit check');
                setMobileMenuOpen(false);
              }}
              className={linkClass}
              style={nunito}
            >
              Fit check
            </a>
            <a
              href='#how-it-works'
              onClick={() => {
                trackNav('How it works');
                setMobileMenuOpen(false);
              }}
              className={linkClass}
              style={nunito}
            >
              How it works
            </a>
            <a
              href='#pricing'
              onClick={() => {
                trackNav('Pricing');
                setMobileMenuOpen(false);
              }}
              className={linkClass}
              style={nunito}
            >
              Pricing
            </a>
            <StartInterviewButton
              location='Header Mobile'
              className='rounded-xl bg-white px-5 py-2.5 text-center font-semibold text-primary'
            >
              Get Started
            </StartInterviewButton>
          </div>
        ) : null}
      </nav>
    </header>
  );
}

function PathCard() {
  return (
    <div className='relative mx-auto w-full max-w-md'>
      <div className='absolute inset-x-6 top-4 h-full rounded-2xl bg-white/20 rotate-3' />
      <div className='absolute inset-x-3 top-2 h-full rounded-2xl bg-white/35 -rotate-2' />
      <div className='relative rounded-2xl bg-white p-8 shadow-2xl'>
        <p className='text-sm font-semibold uppercase tracking-wide text-primary' style={{ ...nunito, fontWeight: 700 }}>
          Your divorce path
        </p>
        <h3 className='mt-2 text-2xl font-bold text-gray-900' style={{ ...nunito, fontWeight: 700 }}>
          Three steps. Start free.
        </h3>
        <ol className='mt-6 space-y-4'>
          {[
            { title: 'See if Harbor may fit', body: 'A few yes-or-no questions.' },
            { title: 'Answer in plain language', body: 'Harbor fills your state’s official forms. Stop and resume any time.' },
            { title: 'You file with the court', body: 'You review the packet. Harbor does not file, serve, or appear for you.' },
          ].map((step, idx) => (
            <li key={step.title} className='flex gap-3'>
              <div className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-white'>
                {idx + 1}
              </div>
              <div>
                <p className='font-semibold text-gray-900' style={{ ...nunito, fontWeight: 700 }}>
                  {step.title}
                </p>
                <p className='text-sm text-gray-600' style={nunito}>
                  {step.body}
                </p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

function FaqSection() {
  const [openName, setOpenName] = useState<string | null>(null);

  return (
    <section id='faq' className='bg-white py-24'>
      <div className='mx-auto max-w-3xl px-6'>
        <h2
          className='mb-12 text-center text-4xl font-bold text-gray-900 md:text-5xl'
          style={{ ...nunito, fontWeight: 700 }}
        >
          Frequently Asked Questions
        </h2>
        <div className='divide-y divide-gray-200 border-y border-gray-200'>
          {faqItems.map((item) => {
            const open = openName === item.name;
            return (
              <div key={item.name}>
                <button
                  type='button'
                  aria-expanded={open}
                  onClick={() => {
                    const next = open ? null : item.name;
                    setOpenName(next);
                    if (next) track('Divorce FAQ Expanded', { question: item.name });
                  }}
                  className='flex w-full items-center justify-between gap-4 py-6 text-left'
                >
                  <span
                    className='text-xl font-semibold text-gray-900 md:text-2xl'
                    style={{ ...nunito, fontWeight: 600 }}
                  >
                    {item.name}
                  </span>
                  <FiChevronDown
                    className={`h-6 w-6 shrink-0 text-gray-500 transition-transform ${
                      open ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {open ? (
                  <p className='pb-6 text-lg leading-relaxed text-gray-600' style={nunito}>
                    {item.text}
                  </p>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function DivorceLandingPage() {
  const { getToken, isLoaded: authLoaded, isSignedIn } = useAuth();
  const { state } = Route.useSearch();
  const [resumeToken, setResumeToken] = useState<string | null>(() =>
    typeof window === 'undefined'
      ? null
      : window.localStorage.getItem(LOCAL_DIVORCE_TOKEN_KEY),
  );
  const stateCopy = state && isPacketState(state) ? STATE_LANDING_COPY[state] : undefined;
  const jurisdiction = stateCopy ? getDivorceJurisdictionByCode(state) : undefined;

  useEffect(() => {
    track('Divorce Landing Viewed', state ? { state } : undefined);
  }, [state]);

  useEffect(() => {
    if (!authLoaded || !isSignedIn) return;
    let cancelled = false;
    void (async () => {
      const response = await apiFetch(getToken, '/v1/divorce/drafts');
      if (!response.ok || cancelled) return;
      const drafts = (await response.json()) as { claim_token: string }[];
      if (!cancelled && drafts[0]?.claim_token) {
        setResumeToken(drafts[0].claim_token);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authLoaded, getToken, isSignedIn]);

  return (
    <DivorceResumeContext.Provider value={resumeToken}>
    <div className='min-h-screen bg-white'>
      <SEO
        title='Harbor Divorce | Free Fit Check + Official Divorce Forms'
        description='Start with a free fit check. If Harbor may help, one $299 case covers petition through judgment, plus 12 months of Harbor Pro. Free checklists for all 50 states. Not legal advice.'
        path='/divorce'
        jsonLd={[faqJsonLd, breadcrumbJsonLd, productJsonLd]}
      />
      <DivorceHeader />

      {resumeToken ? (
        <div className='fixed top-[72px] left-0 right-0 z-40 border-b border-primary/20 bg-white px-4 py-3 shadow-sm'>
          <div className='mx-auto flex max-w-7xl items-center justify-between gap-4'>
            <p className='text-sm font-semibold text-gray-800'>
              You have a saved Harbor Divorce application.
            </p>
            <button
              type='button'
              onClick={() => {
                window.localStorage.removeItem(LOCAL_DIVORCE_TOKEN_KEY);
                setResumeToken(null);
                track('Divorce New Application Chosen');
              }}
              className='shrink-0 text-sm font-semibold text-primary underline'
            >
              Start a new application
            </button>
          </div>
        </div>
      ) : null}

      <section className='relative overflow-x-hidden bg-primary pt-24 md:max-h-[1200px] md:overflow-hidden'>
        <div className='pointer-events-none absolute top-[45%] right-0 bottom-0 left-0 z-0 hidden md:block'>
          <svg viewBox='0 0 1000 800' className='h-full w-full' preserveAspectRatio='none'>
            <defs>
              <linearGradient id='divorceWaveGradient' x1='0%' y1='0%' x2='0%' y2='100%'>
                <stop offset='0%' stopColor='rgba(255, 255, 255, 0.2)' />
                <stop offset='100%' stopColor='rgba(255, 255, 255, 0.05)' />
              </linearGradient>
            </defs>
            <path
              d='M 0,120 Q 125,90 250,120 T 500,120 T 750,120 T 1000,120 L 1000,800 L 0,800 Z'
              fill='url(#divorceWaveGradient)'
              opacity='0.4'
            />
            <path
              d='M 0,120 Q 125,90 250,120 T 500,120 T 750,120 T 1000,120'
              fill='none'
              stroke='rgba(255, 255, 255, 0.25)'
              strokeWidth='3'
              opacity='0.5'
            />
            <path
              d='M 0,140 Q 150,110 300,140 T 600,140 T 900,140 T 1000,140 L 1000,800 L 0,800 Z'
              fill='rgba(255, 255, 255, 0.08)'
              opacity='0.25'
            />
          </svg>
        </div>

        <div className='relative z-10 mx-auto max-w-7xl px-6 pt-16 pb-28 lg:pt-24 lg:pb-40'>
          <div className='grid items-center gap-12 lg:grid-cols-2'>
            <div className='order-1 animate-fade-in lg:order-1'>
              <p
                className='mb-3 text-sm font-semibold uppercase tracking-wide text-cream'
                style={{ ...nunito, fontWeight: 700 }}
              >
                {jurisdiction ? `Harbor Divorce · ${jurisdiction.name}` : 'Harbor Divorce'}
              </p>
              <h1
                className='mb-6 text-5xl leading-tight font-light text-white md:text-6xl lg:text-7xl'
                style={{ ...khula, letterSpacing: '-0.02em' }}
              >
                A clearer way to
                <br />
                start your <span className='text-cream'>divorce.</span>
              </h1>
              <p className='mb-8 max-w-md text-xl text-white/80' style={nunito}>
                You do not need to know the forms, the fees, or the next filing
                yet. Start with a free check that tells you if Harbor may fit.
              </p>
              <StartInterviewButton
                location='Hero'
                className='inline-flex items-center justify-center gap-2 rounded-lg bg-cream px-8 py-4 text-lg font-semibold text-primary transition-all hover:scale-105 hover:bg-cream-dark hover:shadow-xl'
              >
                Get Started
                <FiArrowRight />
              </StartInterviewButton>
            </div>
            <div className='relative z-10 order-2 lg:order-2'>
              <PathCard />
            </div>
          </div>
        </div>

        <div className='absolute right-0 bottom-0 left-0'>
          <svg
            viewBox='0 0 1440 120'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
            className='h-auto w-full'
            preserveAspectRatio='none'
          >
            <path d='M0 120V60C240 20 480 0 720 0C960 0 1200 20 1440 60V120H0Z' fill='#dbeef2' />
          </svg>
        </div>
      </section>

      <section className='bg-sky-light py-24'>
        <div className='mx-auto max-w-7xl px-6'>
          <div className='mb-16 text-center'>
            <span className='mb-2 block text-lg font-semibold text-primary' style={{ ...nunito, fontWeight: 600 }}>
              Start here
            </span>
            <h2
              className='mb-4 text-4xl font-bold text-gray-900 md:text-5xl'
              style={{ ...nunito, fontWeight: 700 }}
            >
              You do not need to know the process yet
            </h2>
            <p className='mx-auto max-w-2xl text-xl text-gray-600' style={nunito}>
              Most people arrive with the same three questions. Harbor answers
              those first, then the paperwork.
            </p>
          </div>
          <div className='grid gap-8 md:grid-cols-3'>
            {[
              {
                icon: FiShield,
                title: 'Can I do this myself?',
                body: 'A short fit check uses the same rules as the rest of Harbor. If Harbor is not the right tool, we say so.',
              },
              {
                icon: FiFileText,
                title: 'What will it cost?',
                body: 'The fit check is free. One $299 case covers petition through judgment. Court filing fees are separate and vary by state; fee waivers exist if you qualify.',
              },
              {
                icon: FiCalendar,
                title: 'What happens next?',
                body: 'If Harbor may fit, you answer in plain language. You review the forms. You file. You can stop before you pay.',
              },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <article key={item.title} className='rounded-2xl bg-white p-8 shadow-sm'>
                  <div className='mb-5 flex h-14 w-14 items-center justify-center rounded-xl bg-primary/10'>
                    <Icon className='h-7 w-7 text-primary' />
                  </div>
                  <h3 className='text-2xl font-bold text-gray-900' style={{ ...nunito, fontWeight: 700 }}>
                    {item.title}
                  </h3>
                  <p className='mt-3 leading-relaxed text-gray-600' style={nunito}>
                    {item.body}
                  </p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section id='fit-check' className='scroll-mt-24 bg-white py-24'>
        <div className='mx-auto max-w-7xl px-6'>
          <div className='grid items-center gap-12 lg:grid-cols-2'>
            <div>
              <span className='mb-2 block text-lg font-semibold text-primary' style={{ ...nunito, fontWeight: 600 }}>
                Free to start
              </span>
              <h2 className='text-4xl font-bold text-gray-900 md:text-5xl' style={{ ...nunito, fontWeight: 700 }}>
                Answer simple questions, then file your divorce
              </h2>
              <p className='mt-6 text-lg leading-relaxed text-gray-600' style={nunito}>
                A few yes-or-no questions about safety, whether you can sign
                together, and whether you are working toward agreement. The
                result is one of three paths: Harbor may fit, you may need to
                wait, or we point you to better help.
              </p>
              <ul className='mt-8 space-y-3 text-gray-800' style={nunito}>
                {[
                  'Uses the same eligibility rules as the rest of Harbor',
                  'Nothing is filed with the court',
                  'You can leave and come back from a saved link',
                ].map((item) => (
                  <li key={item} className='flex gap-3'>
                    <FiCheck className='mt-1 h-5 w-5 shrink-0 text-primary' />
                    {item}
                  </li>
                ))}
              </ul>
              <StartInterviewButton
                location='Fit check'
                className='mt-8 inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-6 py-3 font-semibold text-white transition-colors hover:bg-primary-hover'
              >
                Check my situation
                <FiArrowRight />
              </StartInterviewButton>
            </div>
            <div className='rounded-2xl bg-sky-light p-6 sm:p-8'>
              <p className='text-sm font-semibold uppercase tracking-wide text-primary' style={{ ...nunito, fontWeight: 700 }}>
                A few yes-or-no questions
              </p>
              <div className='mt-4 rounded-2xl bg-white p-6 shadow-sm sm:p-8'>
                <p className='text-xl font-semibold text-gray-900' style={{ ...nunito, fontWeight: 600 }}>
                  Are both of you willing to sign the papers together?
                </p>
                <div className='mt-6 grid grid-cols-2 gap-3'>
                  <div className='rounded-lg bg-primary py-3 text-center font-semibold text-white' style={nunito}>
                    Yes
                  </div>
                  <div className='rounded-lg border border-gray-200 py-3 text-center font-semibold text-gray-700' style={nunito}>
                    No
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className='bg-primary py-20'>
        <div className='mx-auto max-w-7xl px-6'>
          <div className='grid gap-8 md:grid-cols-3 md:gap-12'>
            {[
              {
                icon: FiFileText,
                title: 'Official forms',
                body: 'Your state’s official court forms, not a generic divorce kit.',
              },
              {
                icon: FiRefreshCw,
                title: 'Forms stay current',
                body: 'If your state revises a form, Harbor regenerates your packet for free.',
              },
              {
                icon: FiMessageCircle,
                title: 'Life after the decree',
                body: 'Twelve months of documented chat, expenses, and a custody calendar.',
              },
            ].map((item, index) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  className={`text-center ${
                    index < 2 ? 'md:border-r md:border-white/20 md:pr-8' : ''
                  }`}
                >
                  <div className='mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-white/10'>
                    <Icon className='h-8 w-8 text-white' />
                  </div>
                  <h3 className='mb-3 text-xl font-bold text-white md:text-2xl' style={{ ...nunito, fontWeight: 700 }}>
                    {item.title}
                  </h3>
                  <p className='leading-relaxed text-white/90' style={nunito}>
                    {item.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section id='how-it-works' className='scroll-mt-24 bg-white py-24'>
        <div className='mx-auto max-w-4xl px-6'>
          <div className='mb-16 text-center'>
            <span className='mb-2 block text-lg font-semibold text-primary' style={{ ...nunito, fontWeight: 600 }}>
              No surprises
            </span>
            <h2 className='text-4xl font-bold text-gray-900 md:text-5xl' style={{ ...nunito, fontWeight: 700 }}>
              How the process works
            </h2>
          </div>
          <ol className='space-y-10'>
            {[
              {
                title: 'Check if Harbor may fit',
                body: 'A few yes-or-no questions. Free. If the path is not right, we point you to people who can help.',
              },
              {
                title: 'Answer plain-language questions',
                body: 'Marriage, children, property, and county. Your draft saves as you go. Finish tonight or next week.',
              },
              {
                title: 'Harbor prepares the official forms',
                body: 'Your answers fill your state’s official forms. You review every page before anything is generated.',
              },
              {
                title: 'You file with the court',
                body: 'Print or e-file in the county where you meet residency. Harbor does not file, serve, or appear. Court filing fees are paid to the court and vary by state; fee waivers exist if you qualify.',
              },
            ].map((step, idx) => (
              <li key={step.title} className='flex gap-5'>
                <div className='flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary text-lg font-bold text-white'>
                  {idx + 1}
                </div>
                <div>
                  <h3 className='text-2xl font-bold text-gray-900' style={{ ...nunito, fontWeight: 700 }}>
                    {step.title}
                  </h3>
                  <p className='mt-2 text-lg leading-relaxed text-gray-600' style={nunito}>
                    {step.body}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {stateCopy && jurisdiction ? (
        <section className='bg-sky-light py-24'>
          <div className='mx-auto max-w-7xl px-6'>
            <div className='grid items-start gap-12 lg:grid-cols-2'>
              <div>
                <span className='mb-2 block text-lg font-semibold text-primary' style={{ ...nunito, fontWeight: 600 }}>
                  {stateCopy.eyebrow}
                </span>
                <h2 className='text-4xl font-bold text-gray-900 md:text-5xl' style={{ ...nunito, fontWeight: 700 }}>
                  {stateCopy.heading}
                </h2>
                <p className='mt-6 text-lg leading-relaxed text-gray-600' style={nunito}>
                  {stateCopy.body}
                </p>
                <ul className='mt-8 space-y-3 text-gray-800' style={nunito}>
                  {stateCopy.bullets.map((item) => (
                    <li key={item} className='flex gap-3'>
                      <FiCheck className='mt-1 h-5 w-5 shrink-0 text-primary' />
                      {item}
                    </li>
                  ))}
                </ul>
                <div className='mt-8 flex flex-col gap-3 sm:flex-row'>
                  <StartInterviewButton
                    location='State section'
                    className='inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-6 py-3 font-semibold text-white transition-colors hover:bg-primary-hover'
                  >
                    Check my situation
                    <FiArrowRight />
                  </StartInterviewButton>
                  <Link
                    to='/divorce/$state'
                    params={{ state: jurisdiction.slug }}
                    onClick={() =>
                      track('Divorce Landing State Checklist Clicked', { state: jurisdiction.code })
                    }
                    className='inline-flex items-center justify-center gap-2 rounded-lg border border-primary/30 bg-white px-6 py-3 font-semibold text-primary'
                    style={{ ...nunito, fontWeight: 600 }}
                  >
                    Read the full checklist
                  </Link>
                </div>
              </div>
              <aside className='rounded-2xl border border-gray-100 bg-white p-8 shadow-sm'>
                <h3 className='text-2xl font-bold text-gray-900' style={{ ...nunito, fontWeight: 700 }}>
                  A good fit if
                </h3>
                <ul className='mt-5 space-y-3 text-gray-700' style={nunito}>
                  {stateCopy.goodFit.map((item) => (
                    <li key={item} className='flex gap-3'>
                      <FiCheck className='mt-1 h-5 w-5 shrink-0 text-primary' />
                      {item}
                    </li>
                  ))}
                </ul>
                <h3 className='mt-8 text-2xl font-bold text-gray-900' style={{ ...nunito, fontWeight: 700 }}>
                  Not a replacement for a lawyer
                </h3>
                <ul className='mt-5 space-y-3 text-gray-700' style={nunito}>
                  <li>{stateCopy.counselNote}</li>
                  <li>
                    If you do not feel safe, Harbor will point you to people who
                    can help instead of pushing paperwork.
                  </li>
                </ul>
              </aside>
            </div>
          </div>
        </section>
      ) : null}

      <section id='pricing' className='scroll-mt-24 bg-white py-24'>
        <div className='mx-auto max-w-7xl px-6'>
          <div className='mb-16 text-center'>
            <span className='mb-2 block text-lg font-semibold text-primary' style={{ ...nunito, fontWeight: 600 }}>
              One-time fee
            </span>
            <h2 className='mb-4 text-4xl font-bold text-gray-900 md:text-5xl' style={{ ...nunito, fontWeight: 700 }}>
              Prepare your divorce packet
            </h2>
          </div>

          <div className='mx-auto grid max-w-4xl gap-8 md:grid-cols-2'>
            <div className='relative rounded-2xl border-2 border-primary bg-primary/5 p-8'>
              <div
                className='absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-4 py-1 text-sm font-medium text-white'
                style={nunito}
              >
                Harbor Divorce
              </div>
              <h3 className='mb-2 text-2xl font-bold text-gray-900' style={{ ...nunito, fontWeight: 700 }}>
                One case, start to finish
              </h3>
              <p className='text-4xl font-bold text-gray-900' style={{ ...nunito, fontWeight: 700 }}>
                $299
                <span className='text-lg font-normal text-gray-500'> one time</span>
              </p>
              <ul className='mt-8 mb-8 space-y-3'>
                {[
                  'Petition through judgment — start where you are',
                  'Financial disclosures from statements you drop in',
                  'Forms-only judgment packet from your inventory',
                  'Free regeneration if the court updates its forms',
                  'Bonus: 12 months of Harbor for co-parenting',
                ].map((item) => (
                  <li key={item} className='flex items-center gap-3 text-gray-700' style={nunito}>
                    <FiCheck className='h-5 w-5 shrink-0 text-primary' />
                    {item}
                  </li>
                ))}
              </ul>
              <StartInterviewButton
                location='Pricing'
                className='flex w-full items-center justify-center gap-2 rounded-lg bg-primary py-3 font-semibold text-white transition-colors hover:bg-primary-hover'
              >
                Start free
                <FiArrowRight />
              </StartInterviewButton>
            </div>

            <div className='rounded-2xl border-2 border-gray-200 p-8'>
              <h3 className='mb-2 text-2xl font-bold text-gray-900' style={{ ...nunito, fontWeight: 700 }}>
                Full-service online divorce
              </h3>
              <p className='text-4xl font-bold text-gray-900' style={{ ...nunito, fontWeight: 700 }}>
                $1,500–$5,000+
                <span className='text-lg font-normal text-gray-500'> typical</span>
              </p>
              <ul className='mt-8 space-y-3 text-gray-600' style={nunito}>
                <li>Concierge filing help</li>
                <li>Priced for hand-holding</li>
                <li>Still not a law firm in most cases</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className='bg-sky-light py-24'>
        <div className='mx-auto max-w-7xl px-6'>
          <div className='mb-16 text-center'>
            <span className='mb-2 block text-lg font-semibold text-primary' style={{ ...nunito, fontWeight: 600 }}>
              Included with your Harbor Divorce package
            </span>
            <h2 className='text-4xl font-bold text-gray-900 md:text-5xl' style={{ ...nunito, fontWeight: 700 }}>
              Co-parenting? Get a year of Harbor for free
            </h2>
            <p className='mx-auto mt-4 max-w-2xl text-xl text-gray-600' style={nunito}>
              Don&apos;t overspend, the Harbor app has everything co-parents need
            </p>
          </div>
          <div className='grid gap-8 md:grid-cols-3'>
            {[
              {
                image: harborSsCalendarPng,
                title: 'Custody calendar',
                body: 'The schedule you put in Harbor becomes the week you actually live.',
              },
              {
                image: harborSsChatPng,
                title: 'Documented messages',
                body: 'Keep the hard conversations on the record, with Temperature Check before you send.',
              },
              {
                image: harborSsExportsPng,
                title: 'Court-ready exports',
                body: 'Messages, expenses, and calendar in one report if you need it later.',
              },
            ].map((item) => (
              <article key={item.title} className='overflow-hidden rounded-2xl bg-white shadow-sm'>
                <img
                  src={item.image}
                  alt={item.title}
                  className='h-56 w-full object-contain bg-white p-4'
                  loading='lazy'
                />
                <div className='p-6'>
                  <h3 className='text-xl font-bold text-gray-900' style={{ ...nunito, fontWeight: 700 }}>
                    {item.title}
                  </h3>
                  <p className='mt-2 text-gray-600' style={nunito}>
                    {item.body}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <FaqSection />

      <section className='bg-sky-light py-24'>
        <div className='mx-auto max-w-3xl px-6 text-center'>
          <h2 className='text-4xl font-bold text-gray-900 md:text-5xl' style={{ ...nunito, fontWeight: 700 }}>
            See if Harbor may fit.
          </h2>
          <p className='mx-auto mt-4 max-w-xl text-xl text-gray-600' style={nunito}>
            Answer a few questions now. Pay only if you decide to prepare the
            packet.
          </p>
          <StartInterviewButton
            location='Footer CTA'
            className='mt-8 inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-10 py-4 text-lg font-semibold text-white transition-all hover:bg-primary-hover hover:shadow-xl'
          >
            Get Started
            <FiArrowRight />
          </StartInterviewButton>

          <p className='mx-auto mt-8 max-w-2xl text-sm text-gray-500' style={nunito}>
            {DOC_PREP_DISCLAIMER}
          </p>
        </div>
      </section>

      <Footer />
    </div>
    </DivorceResumeContext.Provider>
  );
}
